<template>
  <TransitionRoot appear as="template" :show="isVisible">
    <Dialog as="div" class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 mt-[-10%]" @click.stop>
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-gray-800">
                <div class="flex items-center justify-between p-6 border-b border-gray-600">
                  <h3 class="text-lg font-semibold leading-6 text-white">Edit Profile</h3>
                  <button type="button" @click="closeDialog" :disabled="isLoading"
                    class="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6">
                  <div class="space-y-6">
                    <div>

                      <div class="flex flex-col items-center space-y-4">
                        <div
                          class="relative h-32 w-32 rounded-full bg-gray-700 border-2 border-gray-600 overflow-hidden cursor-pointer"
                          @click="$refs.fileInput.click()" :class="{ 'border-red-400': errors.image }">
                          <img v-if="previewImage || formData.imageURL" :src="previewImage || formData.imageURL"
                            alt="Profile Preview" class="h-full w-full object-cover" />
                          <div v-else
                            class="flex h-full w-full items-center justify-center text-gray-400 text-sm font-medium">
                            Upload Image
                          </div>
                          <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/jpg"
                            @change="handleImageChange" class="hidden" />
                        </div>
                        <p v-if="errors.image" class="text-sm text-red-400">{{ errors.image }}</p>
                        <p v-if="imageUploading" class="text-sm text-blue-400">Uploading image...</p>
                      </div>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Display Name *</label>
                      <input v-model="formData.displayName" type="text" maxlength="50"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.displayName }" />
                      <p v-if="errors.displayName" class="mt-1 text-sm text-red-400">{{ errors.displayName }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Username *</label>
                      <input v-model="formData.username" type="text" maxlength="30"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.username }" />
                      <p v-if="errors.username" class="mt-1 text-sm text-red-400">{{ errors.username }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Email *</label>
                      <input v-model="formData.email" type="email"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.email }" />
                      <p v-if="errors.email" class="mt-1 text-sm text-red-400">{{ errors.email }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Password</label>
                      <input v-model="formData.password" type="password" maxlength="100"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.password }" placeholder="Leave blank to keep unchanged" />
                      <p v-if="errors.password" class="mt-1 text-sm text-red-400">{{ errors.password }}</p>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                  <button @click="closeDialog" type="button" :disabled="isLoading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 border border-gray-500 rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                  <button @click="handleSubmit" type="button" :disabled="isLoading || imageUploading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <template v-if="isLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </template>
                    <template v-else>Update Profile</template>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref } from 'vue';
import { projectFirestore, projectAuth } from '@/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import useStorage from '@/composables/useStorage';

const { uploadImage, removeImage } = useStorage();

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  userId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['onClose', 'onEmitHandleEvent']);

const isLoading = ref(false);
const imageUploading = ref(false);
const formData = ref({
  displayName: '',
  username: '',
  email: '',
  password: '',
  imageURL: '',
  imagePath: '',
});
const errors = ref({});
const imageFile = ref(null);
const previewImage = ref(null);
const originalEmail = ref('');

const closeDialog = () => {
  formData.value = {
    displayName: '',
    username: '',
    email: '',
    password: '',
    imageURL: '',
    imagePath: '',
  };
  errors.value = {};
  imageFile.value = null;
  previewImage.value = null;
  emit('onEmitHandleEvent', 'close');
};

const handleImageChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      errors.value.image = 'Please upload a jpg or png image';
      imageFile.value = null;
      previewImage.value = null;
      return;
    }
    errors.value.image = '';
    imageFile.value = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      previewImage.value = e.target.result;
    };
    reader.readAsDataURL(file);
  } else {
    imageFile.value = null;
    previewImage.value = null;
  }
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.displayName.trim()) {
    errors.value.displayName = 'Display Name is required';
    isValid = false;
  }
  if (!formData.value.username.trim()) {
    errors.value.username = 'Username is required';
    isValid = false;
  } else if (!/^[a-zA-Z0-9_]+$/.test(formData.value.username)) {
    errors.value.username = 'Username can only contain letters, numbers, and underscores';
    isValid = false;
  }
  if (!formData.value.email.trim()) {
    errors.value.email = 'Email is required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = 'Invalid email format';
    isValid = false;
  }
  if (formData.value.password && formData.value.password.length < 6) {
    errors.value.password = 'Password must be at least 6 characters';
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (isLoading.value || imageUploading.value) return;

  if (!validateForm()) return;

  isLoading.value = true;

  try {
    let imageURL = formData.value.imageURL;
    let imagePath = formData.value.imagePath;
    const currentUser = projectAuth.currentUser;
    
    if (!currentUser) {
      throw new Error('No authenticated user found.');
    }

    if (imageFile.value) {
      imageUploading.value = true;
      const uniqueId = props.userId;
      imagePath = `users/${uniqueId}_${imageFile.value.name}`;

      if (formData.value.imagePath) {
        await removeImage(formData.value.imagePath);
      }

      imageURL = await uploadImage(imagePath, imageFile.value);
      if (!imageURL) throw new Error('Image upload failed');
    }

    const profileData = {
      displayName: formData.value.displayName.trim(),
      username: formData.value.username.trim(),
      email: formData.value.email.trim(),
      imageURL: imageURL || '',
      updatedAt: serverTimestamp(),
    };

    // Update Firebase Auth profile (displayName and photoURL don't require recent login)
    await updateProfile(currentUser, {
      displayName: profileData.displayName,
      photoURL: profileData.imageURL,
    });

    // Save to Firestore first (this doesn't require recent login)
    const userRef = doc(projectFirestore, 'users', props.userId);
    await setDoc(userRef, profileData, { merge: true });

    // Track if any sensitive operations failed
    let sensitiveUpdateWarning = null;

    // Update email if changed (requires recent login)
    const emailChanged = formData.value.email.trim() !== originalEmail.value;
    if (emailChanged) {
      try {
        await updateEmail(currentUser, profileData.email);
      } catch (emailError) {
        if (emailError.code === 'auth/requires-recent-login') {
          sensitiveUpdateWarning = 'Email change requires recent login. Please log out and log back in to change your email.';
        } else {
          throw emailError;
        }
      }
    }

    // Update password if provided (requires recent login)
    if (formData.value.password && formData.value.password.trim()) {
      try {
        await updatePassword(currentUser, formData.value.password.trim());
      } catch (passwordError) {
        if (passwordError.code === 'auth/requires-recent-login') {
          const warning = 'Password change requires recent login. Please log out and log back in to change your password.';
          sensitiveUpdateWarning = sensitiveUpdateWarning 
            ? `${sensitiveUpdateWarning} ${warning}` 
            : warning;
        } else {
          throw passwordError;
        }
      }
    }

    // If there were warnings but other updates succeeded, emit updated with warning
    if (sensitiveUpdateWarning) {
      emit('onEmitHandleEvent', 'updated', false, sensitiveUpdateWarning);
    } else {
      emit('onEmitHandleEvent', 'updated');
    }
  } catch (error) {
    console.error('Error updating profile:', error);
    const errorMessage = error.message || 'Failed to update profile. Please try again.';
    emit('onEmitHandleEvent', 'error', true, errorMessage);
  } finally {
    isLoading.value = false;
    imageUploading.value = false;
    previewImage.value = null;
  }
};

// Fetch existing profile data
const fetchProfile = async () => {
  try {
    const userRef = doc(projectFirestore, 'users', props.userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      formData.value = { ...formData.value, ...userDoc.data() };
    }
    const currentUser = projectAuth.currentUser;
    if (currentUser) {
      formData.value.email = currentUser.email || formData.value.email;
      formData.value.displayName = currentUser.displayName || formData.value.displayName;
      formData.value.imageURL = currentUser.photoURL || formData.value.imageURL;
      // Store original email for comparison
      originalEmail.value = currentUser.email || formData.value.email || '';
    }
  } catch (error) {
    console.error('Error fetching profile:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to load profile data.');
  }
};
fetchProfile();
</script>

<style scoped>
input,
select,
textarea {
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #3b82f6;
}

button:disabled {
  cursor: not-allowed;
}

.rounded-full {
  border-radius: 9999px;
}

.relative:hover .text-gray-400 {
  background-color: rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s ease;
}
</style>